/**
 * O que numa peça está pendente de gravação (E84.F7 / GUS-45).
 *
 * **Por que existe**: a página guarda o rascunho do código em `codigosEditados`
 * e o do texto em `observacoesEditadas`, mas quem decide se aquilo é *mudança*
 * é o servidor, no próprio UPDATE (`_valores_do_update`, E84.F5.T01): só código
 * ou tipo efetivamente diferentes do gravado invalidam a verificação;
 * observação nunca invalida. Sem medir do mesmo jeito, a tela travaria um
 * `Verificar` que o servidor aceitaria — ou, pior, liberaria um que o servidor
 * invalidaria logo em seguida.
 *
 * Por isso o predicado daqui é o do servidor, transcrito: o valor que o PUT
 * enviaria, comparado ao gravado, com **vazio e nulo equivalentes**.
 *
 * O tipo da peça não entra: ele não é editável na tela — é lido do rótulo do
 * comprovante (E84.F1) e do cabeçalho da própria peça (E86.F1) —, então a única
 * alteração pendente que a tela pode produzir é a do código.
 */
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico';
import { normalizarCodigo } from '@/lib/exame_eletronico_peca';

/** O valor que o PUT enviaria em `codigo_declarado`: normalizado, ou nulo. */
export function codigoParaGravar(rascunho: string): string | null {
  const normalizado = normalizarCodigo(rascunho);
  return normalizado.length > 0 ? normalizado : null;
}

/** O valor que o PUT enviaria em `observacoes`: aparado, ou nulo. */
export function observacoesParaGravar(rascunho: string): string | null {
  const texto = rascunho.trim();
  return texto.length > 0 ? texto : null;
}

/**
 * O rascunho do código difere do gravado — a alteração que INVALIDA a
 * verificação anterior (RN02).
 */
export function codigoAlterado(peca: ExameEletronicoCcbData, rascunho: string): boolean {
  return codigoParaGravar(rascunho) !== (peca.codigo_declarado ?? null);
}

/**
 * O rascunho do texto difere do gravado. Conta para habilitar o `Salvar`
 * (RN04), e **não** para travar a verificação: observação não invalida (RN03).
 */
export function observacoesAlteradas(peca: ExameEletronicoCcbData, rascunho: string): boolean {
  return observacoesParaGravar(rascunho) !== (peca.observacoes_peca ?? null);
}

/**
 * Os `source_document_id` cujo código em tela não é o gravado.
 *
 * É esta lista — e não "algum rascunho existe" — que trava o `Verificar`: um
 * rascunho idêntico ao gravado não é alteração nenhuma, e a observação fica de
 * fora de propósito (RN03).
 *
 * Chave ausente é **sem rascunho**, não rascunho vazio: a peça em que ninguém
 * digitou mostra o gravado, e apagar um código é outra coisa — essa chega como
 * string vazia e conta como alteração.
 */
export function pecasComCodigoAlterado(
  pecas: readonly ExameEletronicoCcbData[],
  rascunhos: Readonly<Record<string, string>>,
): readonly string[] {
  return pecas
    .filter((peca) => {
      const rascunho = rascunhos[peca.source_document_id];
      return rascunho !== undefined && codigoAlterado(peca, rascunho);
    })
    .map((peca) => peca.source_document_id);
}

/**
 * Há mudança real a gravar nesta peça — código OU texto (RN04).
 *
 * O `Salvar` da caixa da peça envia os dois no mesmo PUT, então ele acende com
 * qualquer um dos dois; e o texto conta aqui embora não invalide verificação
 * nenhuma, porque "mudança real" e "mudança que invalida" são perguntas
 * diferentes (RN03 responde só a segunda).
 */
export function temMudancaReal(
  peca: ExameEletronicoCcbData,
  codigo: string,
  observacoes: string,
): boolean {
  return codigoAlterado(peca, codigo) || observacoesAlteradas(peca, observacoes);
}
